"use client";

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { Mail, Lock, Globe, ArrowRight, Eye, EyeOff, ShieldCheck, AlertCircle } from 'lucide-react';
import { apiService } from '@/services/api';

const i18n = {
  ar: {
    titleNew: 'مرحباً بك في كلمني',
    titleReturn: 'مرحباً بعودتك',
    subtitle: 'سجّل دخولك للوصول إلى لوحة التحكم',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    passwordLabel: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    forgotPassword: 'نسيت كلمة المرور؟',
    signInBtn: 'تسجيل الدخول',
    footer: 'بوابة آمنة ومشفرة',
    targetLang: 'English',
    notAllowed:       'عذراً، هذه البوابة مخصصة للإدارة والأخصائيين فقط.',
    wrongCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    emailNotFound:    'البريد الإلكتروني غير مسجل لدينا.',
    accountSuspended: 'هذا الحساب موقوف، يرجى التواصل مع الإدارة.',
    serverError:      'حدث خطأ في الاتصال بالسيرفر. يرجى المحاولة لاحقاً.',
    resetLinkSent:    'تم إرسال رابط إعادة تعيين كلمة المرور بنجاح.',
    enterEmailFirst:  'يرجى كتابة بريدك الإلكتروني أولاً.',
    errorReset:       'حدث خطأ أثناء محاولة إرسال رابط التعيين.',
  },
  en: {
    titleNew: 'Welcome to Kalimni',
    titleReturn: 'Welcome Back',
    subtitle: 'Sign in to access your dashboard',
    emailLabel: 'Email Address',
    emailPlaceholder: 'Enter your email',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    forgotPassword: 'Forgot password?',
    signInBtn: 'Sign In',
    footer: 'Secure & Encrypted Gateway',
    targetLang: 'العربية',
    notAllowed:       'This portal is for admins and specialists only.',
    wrongCredentials: 'Incorrect email or password.',
    emailNotFound:    'This email is not registered.',
    accountSuspended: 'This account is suspended. Please contact support.',
    serverError:      'A server error occurred. Please try again.',
    resetLinkSent:    'A password reset link has been sent to your email.',
    enterEmailFirst:  'Please enter your email address first.',
    errorReset:       'An error occurred while sending the reset link.',
  },
};

function localizeError(msg: string, tr: typeof i18n['ar']): string {
  const m = (msg ?? '').toLowerCase();
  if (m.includes('incorrect') || m.includes('wrong') || m.includes('password') ||
      m.includes('credentials') || m.includes('غير صحيح') || m.includes('كلمة المرور'))
    return tr.wrongCredentials;
  if (m.includes('not registered') || m.includes('not found') || m.includes('غير مسجل') || m.includes('غير موجود'))
    return tr.emailNotFound;
  if (m.includes('suspended') || m.includes('banned') || m.includes('موقوف') || m.includes('محظور'))
    return tr.accountSuspended;
  if (m.includes('portal') || m.includes('specialist') || m.includes('admin') || m.includes('مخصصة'))
    return tr.notAllowed;
  return tr.serverError;
}

export default function LoginPage() {
  const pathname = usePathname();

  const [lang, setLang] = useState<'ar' | 'en'>('ar');

  useEffect(() => {
    const htmlLang = document.documentElement.lang;
    if (htmlLang === 'en') { setLang('en'); return; }
    if (htmlLang === 'ar') { setLang('ar'); return; }

    const stored = localStorage.getItem('NEXT_LOCALE');
    if (stored === 'en') { setLang('en'); return; }
    if (stored === 'ar') { setLang('ar'); return; }

    const url = window.location.href;
    if (url.includes('/en/') || url.includes('/en?') || url.endsWith('/en')) {
      setLang('en');
    }
  }, []);

  const tr    = i18n[lang];
  const isRTL = lang === 'ar';

  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isReturning, setIsReturning] = useState<boolean | null>(null);

  useEffect(() => {
    const hasVisited = localStorage.getItem('has_visited_before');
    if (!hasVisited) localStorage.setItem('has_visited_before', 'true');
    setIsReturning(!!hasVisited);
  }, []);

  const switchLanguage = () => {
    if (!pathname) return;
    const target = lang === 'ar' ? 'en' : 'ar';
    const newPath = pathname.replace(`/${lang}`, `/${target}`);
    document.cookie = `NEXT_LOCALE=${target}; path=/; max-age=31536000; SameSite=Lax`;
    localStorage.setItem('NEXT_LOCALE', target);
    window.location.href = newPath;
  };

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!email || !email.includes('@')) { alert(tr.enterEmailFirst); return; }
    try {
      setIsSubmitting(true);
      await apiService.sendPasswordResetEmail(email.trim(), lang);
      alert(tr.resetLinkSent);
    } catch (err: any) {
      setErrorMessage(localizeError(err.message ?? '', tr));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await apiService.login({ email: email.trim(), password, lang });
      const user = response.user;
      const role = (user?.role || '').toLowerCase().trim();
      const isAdmin      = role.includes('admin') || role === 'administrator';
      const isSpecialist = role === 'specialist';

      if (isAdmin || isSpecialist) {
        let preferredLang: 'ar' | 'en' = lang;
        const saved = localStorage.getItem('NEXT_LOCALE');
        if (saved === 'en' || saved === 'ar') preferredLang = saved as 'ar' | 'en';

        // ✅ حط الـ cookies عن طريق الـ server-side API Route
        await fetch('/api/auth/set-cookies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_info: user,
            auth_token: response.token,
            locale: preferredLang,
          }),
        });

        // احتياطي: خزّن بالـ localStorage كمان
        if (response.token) localStorage.setItem('auth_token', response.token);
        localStorage.setItem('NEXT_LOCALE', preferredLang);

        const FRONTEND_URL = window.location.origin;
        window.location.href = isAdmin
          ? `${FRONTEND_URL}/${preferredLang}/admin`
          : `${FRONTEND_URL}/${preferredLang}/specialist`;
      } else {
        setErrorMessage(tr.notAllowed);
        setIsSubmitting(false);
      }
    } catch (error: any) {
      setErrorMessage(localizeError(error.message ?? '', tr));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans bg-[#F8F9FC] relative overflow-hidden text-start selection:bg-[#352C5E]/10">

      <div className="absolute top-6 end-6 z-50">
        <button onClick={switchLanguage} type="button"
          className="flex items-center gap-2.5 px-5 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-bold text-gray-800 hover:bg-[#352C5E] hover:text-white transition-all shadow-sm">
          <Globe size={18} className="text-[#E1AD48]" />
          <span>{tr.targetLang}</span>
        </button>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 bg-[#F8F9FC]">
        <div className="w-full max-w-[420px]">

          <div className="flex justify-center mb-12">
            <div className="p-1.5 rounded-[1.5rem] bg-gradient-to-br from-[#BF9B30] to-[#F3D088]">
              <div className="p-1 rounded-[1.2rem] bg-[#352C5E]">
                <div className="bg-white px-5 py-4 rounded-[1rem]">
                  <Image src="/logo.png" alt="Logo" width={130} height={40} priority />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-10 text-center">
            <h1 className="text-3xl font-extrabold text-[#352C5E] mb-3 leading-tight min-h-[2.5rem]">
              {isReturning === null
                ? '\u00A0'
                : isReturning
                  ? tr.titleReturn
                  : tr.titleNew}
            </h1>
            <p className="text-gray-600 font-semibold">{tr.subtitle}</p>
          </div>

          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3 text-sm font-bold animate-in fade-in zoom-in-95 duration-200">
              <AlertCircle size={18} className="shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest">{tr.emailLabel}</label>
              <div className="relative group">
                <Mail size={20} className="absolute start-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-[#7D65CC] z-10" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder={tr.emailPlaceholder} required
                  className={`w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-4 text-gray-900 font-bold placeholder:text-gray-400 placeholder:font-medium outline-none focus:border-[#7D65CC] focus:ring-4 focus:ring-[#7D65CC]/5 transition-all ${isRTL ? 'text-right pr-12' : 'pl-12'}`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest">{tr.passwordLabel}</label>
                <button type="button" onClick={handleForgotPassword} disabled={isSubmitting}
                  className="text-xs font-bold text-[#E1AD48] hover:text-[#352C5E] transition-colors disabled:opacity-50">
                  {tr.forgotPassword}
                </button>
              </div>
              <div className="relative group">
                <Lock size={20} className="absolute start-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-[#7D65CC] z-10" />
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder={tr.passwordPlaceholder} required
                  className={`w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-4 text-gray-900 font-bold placeholder:text-gray-400 placeholder:font-medium outline-none focus:border-[#7D65CC] focus:ring-4 focus:ring-[#7D65CC]/5 transition-all ${isRTL ? 'text-right pr-12 pl-12' : 'pl-12 pr-12'}`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#352C5E] transition-colors">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-3 py-4 bg-[#352C5E] text-white rounded-xl font-black text-base shadow-xl hover:bg-[#2A234C] transition-all active:scale-[0.98] disabled:opacity-70">
              {isSubmitting ? <LoaderSpinner /> : (
                <><span>{tr.signInBtn}</span><ArrowRight size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''} /></>
              )}
            </button>
          </form>

          <div className="mt-12 text-center border-t border-gray-200 pt-6">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <ShieldCheck size={18} className="text-[#E1AD48]" />
              <p className="text-[11px] font-black uppercase tracking-tighter">{tr.footer}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative p-4">
        <div className="relative w-full h-full rounded-[2.5rem] overflow-hidden bg-gray-100 shadow-2xl">
          <Image src="/login.png" alt="Cover" fill priority className="object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#352C5E]/20 to-transparent" />
        </div>
      </div>
    </div>
  );
}

function LoaderSpinner() {
  return <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />;
}